import { Host, h } from "@stencil/core";
import { getReadableFileSizeString } from "../../utilities/stringUtilities";
export class DnnDropzone {
    constructor() {
        /**
         * If true, will allow the user to take a snapshot
         * using the device camera. (only works over https).
         */
        this.allowCameraMode = false;
        /**
         * Specifies the jpeg quality for when the device
         * camera is used to generate a picture.
         * Needs to be a number between 0 and 1 and defaults to 0.8
         */
        this.captureQuality = 0.8;
        this.canTakeSnapshots = false;
        this.takingPicture = false;
        this.fileTooLarge = false;
        this.invalidExtension = false;
        this.focused = false;
        this.defaultResx = {
            dragAndDropFile: "Drag and drop a file",
            capture: "Capture",
            or: "or",
            takePicture: "Take a picture",
            uploadFile: "Upload a file",
            uploadSizeTooLarge: "The file you tried to upload is too large.",
            fileSizeLimit: "The maximum size is {0}",
            invalidExtension: "The file you tried to upload has an invalid extension.",
            allowedFileExtensions: "Allowed file extensions: {0}",
        };
        this.handleDragOver = (event) => {
            event.stopPropagation();
            event.preventDefault();
            if (event.dataTransfer != null) {
                event.dataTransfer.dropEffect = "copy";
            }
            this.dropzone.classList.add("dropping");
        };
        this.handleDrop = (dropEvent) => {
            this.invalidExtension = false;
            this.fileTooLarge = false;
            dropEvent.stopPropagation();
            dropEvent.preventDefault();
            const files = dropEvent.dataTransfer.files;
            if (this.hasInvalidExtensions(Array.from(files))) {
                this.invalidExtension = true;
                return;
            }
            if (this.isAnyFileLargerThanAllowed(Array.from(files))) {
                this.fileTooLarge = true;
                return;
            }
            var fileList = this.getFilesFromFileList(files);
            this.filesSelected.emit(fileList);
        };
    }
    componentWillLoad() {
        this.mergeResx();
    }
    componentDidLoad() {
        if (this.allowCameraMode) {
            this.checkIfBrowserCanTakeSnapshots()
                .then(result => this.canTakeSnapshots = result);
        }
        if (this.allowedExtensions != undefined && this.allowedExtensions.length > 0) {
            var extensionsWithDots = this.allowedExtensions.map(e => `.${e}`);
            var extensionsList = extensionsWithDots.join(",");
            this.fileInput.accept = extensionsList;
        }
    }
    resxChanged() {
        this.mergeResx();
    }
    formResetCallback() {
        this.internals.setValidity({});
        this.fileInput.value = "";
        this.internals.setFormValue("");
    }
    mergeResx() {
        this.localResx = Object.assign(Object.assign({}, this.defaultResx), this.resx);
    }
    checkIfBrowserCanTakeSnapshots() {
        return new Promise((resolve) => {
            const mediaDevices = navigator.mediaDevices;
            if (mediaDevices == undefined || mediaDevices.enumerateDevices == undefined) {
                resolve(false);
            }
            mediaDevices.enumerateDevices()
                .then(devices => {
                var result = devices.some(device => device.kind == "videoinput");
                resolve(result);
            });
        });
    }
    getFilesFromFileList(files) {
        var fileList = [];
        if (files == undefined) {
            return fileList;
        }
        for (let index = 0; index < files.length; index++) {
            const file = files[index];
            fileList.push(file);
        }
        return fileList;
    }
    isAnyFileLargerThanAllowed(files) {
        if (this.maxFileSize == undefined || this.maxFileSize <= 0) {
            return false;
        }
        if (this.maxFileSize != undefined && files.some(file => file.size > this.maxFileSize)) {
            return true;
        }
        return false;
    }
    handleUploadKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.fileInput.click();
        }
    }
    handleUploadButton(element) {
        this.fileTooLarge = false;
        this.invalidExtension = false;
        let files = this.getFilesFromFileList(element.files);
        if (this.isAnyFileLargerThanAllowed(files)) {
            this.fileTooLarge = true;
            return;
        }
        if (this.hasInvalidExtensions(files)) {
            this.invalidExtension = true;
            return;
        }
        this.filesSelected.emit(files);
        if (this.name != undefined && this.name.length > 0) {
            var data = new FormData();
            files.forEach(file => {
                data.append(this.name || "", file);
            });
            this.internals.setFormValue(data);
        }
    }
    hasInvalidExtensions(files) {
        var _a, _b;
        var hasInvalid = false;
        for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
            const file = files[fileIndex];
            var regex = /(?:\.([^.]+))?$/;
            const fileExtension = (_a = regex.exec(file.name)[1]) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            if (fileExtension == undefined) {
                hasInvalid = true;
            }
            var loweredAllowedExtensions = (_b = this.allowedExtensions) === null || _b === void 0 ? void 0 : _b.map(e => e.toLowerCase());
            if (this.allowedExtensions != undefined && !(loweredAllowedExtensions === null || loweredAllowedExtensions === void 0 ? void 0 : loweredAllowedExtensions.includes(fileExtension))) {
                hasInvalid = true;
            }
            return hasInvalid;
        }
        return false;
    }
    takeSnapshot() {
        this.takingPicture = true;
        navigator.mediaDevices.getUserMedia({ video: true, audio: false })
            .then(stream => {
            stream;
            this.videoPreview.srcObject = stream;
            this.videoPreview.play()
                .then(() => {
                this.videoSettings = stream.getVideoTracks()[0].getSettings();
            });
        })
            .catch(error => alert(error));
    }
    applySnapshot() {
        var canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = this.videoSettings.width;
        canvas.height = this.videoSettings.height;
        context.drawImage(this.videoPreview, 0, 0);
        canvas.toBlob(blob => {
            var file = new File([blob], "image.jpeg", { type: "image/jpeg" });
            this.takingPicture = false;
            var fileList = [file];
            this.filesSelected.emit(fileList);
        }, "image/jpeg", this.captureQuality);
    }
    getInvalidExtensionsMessage() {
        var message = this.localResx.allowedFileExtensions;
        var message = message.replace("{0}", this.allowedExtensions.join(", "));
        return message;
    }
    render() {
        var _a, _b, _c, _d, _e, _f;
        return (h(Host, { key: '9bf5683b9676b9f12bfdd3d7cb0c83256f8c0203', ref: e => this.dropzone = e, class: "dropzone", onDragOver: (e) => this.handleDragOver(e), onDrop: (e) => this.handleDrop(e), onDragLeave: () => this.dropzone.classList.remove("dropping"), tabIndex: this.focused ? -1 : 0, onFocus: () => this.uploadLabel.focus(), onBlur: () => this.uploadLabel.blur() }, !this.takingPicture &&
            [
                h("p", { key: '1f20574a079f352ce1689d49e8a1dd24a9c99273' }, (_a = this.localResx) === null || _a === void 0 ? void 0 : _a.dragAndDropFile),
                h("p", { key: 'f10c10c4ff5aac3f49ee79879c2b01f06ad9c217' }, "- ", (_b = this.localResx) === null || _b === void 0 ? void 0 :
                    _b.or, " -"),
                h("label", { key: 'dfabce2f1514f4c64b348a68429e4a4a164fdf65', class: "upload-file", tabIndex: 0, onKeyDown: e => this.handleUploadKeyDown(e), ref: el => this.uploadLabel = el, onFocus: () => this.focused = true, onBlur: () => this.focused = false }, h("input", { key: 'fdb1cbf34bafaee686cfda2c8e2e1f4d1d7557d8', type: "file", ref: el => this.fileInput = el, onChange: e => this.handleUploadButton(e.target) }), h("span", { key: '003231306c4a995678f8c41bbcb0d0667f5312d4' }, h("svg", { key: '8f7f50f075dff8a690cc7f6b43dcedb2d68c2802', xmlns: "http://www.w3.org/2000/svg", "enable-background": "new 0 0 24 24", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("g", { key: 'bc713b2e80a36383680654fcf944b4447808f8e4' }, h("rect", { key: 'a0a8812fc7ac49f95cd3b885232e393d6da40628', fill: "none", height: "24", width: "24" })), h("g", { key: 'c38ff3ab4708733194c4d98f2f725387ac1da991' }, h("path", { key: '35034aedf3fc0dec87e950fc296cb442bfabaae5', d: "M5,20h14v-2H5V20z M5,10h4v6h6v-6h4l-7-7L5,10z" })))), "\u00A0", (_c = this.localResx) === null || _c === void 0 ? void 0 :
                    _c.uploadFile),
                this.canTakeSnapshots &&
                    [
                        h("p", { key: '93f0444250ea6e18e8e5bd9af758ebf38a44d97a' }, "- ", (_d = this.localResx) === null || _d === void 0 ? void 0 :
                            _d.or, " -"),
                        h("button", { key: '5e78937c6ba6eaea45bbe12ffe43583a909c64be', onClick: () => this.takeSnapshot() }, h("svg", { key: 'a3f342027510b4bc4d5c1a17c8c4582f59e8c901', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '5d598483b5944434a674d97249981c3227e7100d', d: "M0 0h24v24H0z", fill: "none" }), h("circle", { key: 'f42859aaf900907f711dcc91dce9845fc6ad20ed', cx: "12", cy: "12", r: "3.2" }), h("path", { key: 'cb210b970f117a381172a767556b80d8f05158ab', d: "M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" })), (_e = this.localResx) === null || _e === void 0 ? void 0 :
                            _e.takePicture)
                    ]
            ], this.takingPicture &&
            h("div", { key: '7a96086948279dba16838d4f78324f38ef679a45', class: "video-preview" }, h("video", { key: 'c22a04d71071bd1267946cc9de99b2e602be832e', ref: e => this.videoPreview = e }), h("button", { key: '5d0d4c8f918fe2f7e14a19853fce505dd5cc0f79', onClick: () => this.applySnapshot() }, h("svg", { key: 'fb4b54a29b90e12c7587fa2a2bdb521cb5f2291b', xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 0 24 24", width: "24px", fill: "#000000" }, h("path", { key: '62fe7540d09aa2d84a1b0ba7bd8a796c885a6ff0', d: "M0 0h24v24H0z", fill: "none" }), h("circle", { key: 'e26d2dd296c55809b2a80f4e089e4fc6e6ab9281', cx: "12", cy: "12", r: "3.2" }), h("path", { key: 'a4c67d37680f7852831e1fd8bd1a737f89c30633', d: "M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" })), "\u00A0", (_f = this.localResx) === null || _f === void 0 ? void 0 :
                _f.capture)), this.fileTooLarge &&
            h("div", { key: '29a3f6ecf6bade110ac9dc6d5fdf71d6b0a0afe6', class: 'error' }, h("p", { key: '04fb742f8c8ae23e263cb6e0d84154a82be5b0b1' }, this.localResx.uploadSizeTooLarge, h("br", { key: '98deac095ecdc88db09ee4db0020ae1965dcf855' }), this.localResx.fileSizeLimit.replace("{0}", getReadableFileSizeString(this.maxFileSize)))), this.invalidExtension &&
            h("div", { key: '14cc9810bc03af3aa52c4c320e351aef1d313b2c', class: 'error' }, h("p", { key: '017fc0253778bb478602f27cbd80e18f4d63ab19' }, this.localResx.invalidExtension, h("br", { key: '50c241772f10bb66fb97075afa6967e63536c3a1' }), this.getInvalidExtensionsMessage()))));
    }
    static get is() { return "dnn-dropzone"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-dropzone.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-dropzone.css"]
        };
    }
    static get properties() {
        return {
            "resx": {
                "type": "unknown",
                "attribute": "resx",
                "mutable": false,
                "complexType": {
                    "original": "DropzoneResx",
                    "resolved": "DropzoneResx | undefined",
                    "references": {
                        "DropzoneResx": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/dnn-dropzone/types.ts::DropzoneResx"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Localization strings"
                },
                "getter": false,
                "setter": false
            },
            "allowedExtensions": {
                "type": "unknown",
                "attribute": "allowed-extensions",
                "mutable": false,
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[] | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "A list of allowed file extensions.\nIf not specified, any file is allowed.\nEx: [\"jpg\", \"jpeg\", \"gif\", \"png\"]"
                },
                "getter": false,
                "setter": false
            },
            "allowCameraMode": {
                "type": "boolean",
                "attribute": "allow-camera-mode",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, will allow the user to take a snapshot\nusing the device camera. (only works over https)."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "captureQuality": {
                "type": "number",
                "attribute": "capture-quality",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the jpeg quality for when the device\ncamera is used to generate a picture.\nNeeds to be a number between 0 and 1 and defaults to 0.8"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "0.8"
            },
            "maxFileSize": {
                "type": "number",
                "attribute": "max-file-size",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Max file size in bytes."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "name": {
                "type": "string",
                "attribute": "name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name of the field when used in a form."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "canTakeSnapshots": {},
            "takingPicture": {},
            "fileTooLarge": {},
            "invalidExtension": {},
            "localResx": {},
            "focused": {}
        };
    }
    static get events() {
        return [{
                "method": "filesSelected",
                "name": "filesSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fires when file were selected."
                },
                "complexType": {
                    "original": "File[]",
                    "resolved": "File[]",
                    "references": {
                        "File": {
                            "location": "global",
                            "id": "global::File"
                        }
                    }
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "resx",
                "methodName": "resxChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=dnn-dropzone.js.map
